import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

// Topic → inbox routing. Kept server-side so a hostile client can't redirect
// support messages to an arbitrary address.
export const SUPPORT_TOPICS = [
  "General enquiry",
  "Member support",
  "Therapist applications",
  "Account & login",
  "Billing & subscription",
  "Technical issue",
  "Safety concern",
  "Content feedback",
  "Other",
] as const;

export type SupportTopic = (typeof SUPPORT_TOPICS)[number];

const INFO = "info@recoverytrauma.com";
const SUPPORT = "support@recoverytrauma.com";
const THERAPISTS = "therapists@recoverytrauma.com";

export function routeForTopic(topic: string): string {
  switch (topic) {
    case "General enquiry":
    case "Content feedback":
    case "Other":
      return INFO;
    case "Therapist applications":
      return THERAPISTS;
    case "Member support":
    case "Account & login":
    case "Billing & subscription":
    case "Technical issue":
    case "Safety concern":
      return SUPPORT;
    default:
      return INFO;
  }
}

const inputSchema = z.object({
  name: z.string().trim().max(120).optional().nullable(),
  email: z.string().trim().email().max(255),
  message: z.string().trim().min(5, "Message is too short").max(5000),
  // Optional context from the in-app support chat.
  topic: z.string().trim().max(120).optional().nullable(),
  transcript: z.string().trim().max(8000).optional().nullable(),
});

function esc(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );
}

export const submitSupportRequest = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => inputSchema.parse(raw))
  .handler(async ({ data }) => {
    // All Contact Support messages route to a single inbox.
    const routed = INFO;

    // Chat escalations arrive with a topic and (optionally) the conversation
    // so the team has context without asking the person to repeat themselves.
    const transcript = data.transcript?.trim() || null;
    const storedMessage = transcript
      ? `${data.message}\n\n--- Support chat transcript ---\n${transcript}`
      : data.message;

    // Store in Lovable Cloud (bypasses RLS — public form).
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: inserted, error } = await supabaseAdmin
      .from("support_requests")
      .insert({
        name: data.name || null,
        email: data.email,
        topic: data.topic?.trim() || "General enquiry",
        message: storedMessage,
        routed_to: routed,
      })
      .select("id")
      .single();
    if (error) {
      console.error("[support] insert failed", error);
      throw new Error("We couldn't save your message. Please try again.");
    }

    // Enqueue the notification email through the Lovable email queue. Failure
    // here should not lose the ticket — it's already stored in the admin inbox.
    try {
      const subject = `Support request from ${data.name || data.email}`;
      const html = `<!doctype html><html><body style="font-family:Arial,sans-serif;background:#fff;color:#1a1a1a;margin:0;padding:0">
<div style="max-width:580px;margin:0 auto;padding:32px 24px">
  <h1 style="font-size:20px;margin:0 0 16px">New support request</h1>
  <table style="border-collapse:collapse;font-size:14px;line-height:1.7">
    <tr><td style="padding:4px 12px 4px 0;color:#666">Name</td><td>${esc(data.name || "—")}</td></tr>
    <tr><td style="padding:4px 12px 4px 0;color:#666">Email</td><td><a href="mailto:${esc(data.email)}">${esc(data.email)}</a></td></tr>
    <tr><td style="padding:4px 12px 4px 0;color:#666;vertical-align:top">Ticket</td><td><code>${esc(inserted.id)}</code></td></tr>
    ${data.topic ? `<tr><td style="padding:4px 12px 4px 0;color:#666">Topic</td><td>${esc(data.topic)}</td></tr>` : ""}
  </table>
  <hr style="border:none;border-top:1px solid #eee;margin:20px 0"/>
  <p style="line-height:1.6;white-space:pre-wrap;margin:0 0 12px">${esc(data.message)}</p>
  ${
    transcript
      ? `<hr style="border:none;border-top:1px solid #eee;margin:20px 0"/>
  <p style="font-size:12px;color:#888;margin:0 0 8px">Support chat transcript</p>
  <p style="line-height:1.6;white-space:pre-wrap;margin:0;font-size:13px;color:#444">${esc(transcript)}</p>`
      : ""
  }
  <hr style="border:none;border-top:1px solid #eee;margin:20px 0"/>
  <p style="font-size:12px;color:#888;margin:0">Reply directly to this email to respond to the sender.</p>
</div></body></html>`;
      const text = `New support request\n\nName: ${data.name || "—"}\nEmail: ${data.email}\nTicket: ${inserted.id}${data.topic ? `\nTopic: ${data.topic}` : ""}\n\n${data.message}${transcript ? `\n\n--- Support chat transcript ---\n${transcript}` : ""}\n\nReply directly to this email to respond to the sender.`;

      const messageId = `support-${inserted.id}`;
      await supabaseAdmin.from("email_send_log").insert({
        message_id: messageId,
        template_name: "support_request",
        recipient_email: routed,
        status: "pending",
      });

      // Upsert a stable unsubscribe token for the internal recipient inbox so
      // the transactional email API accepts the send. Internal support inboxes
      // won't ever click unsubscribe, but the API requires the token.
      let unsubscribeToken: string | null = null;
      try {
        const { data: existing } = await supabaseAdmin
          .from("email_unsubscribe_tokens" as never)
          .select("token")
          .eq("email", routed)
          .maybeSingle();
        const existingTok = (existing as unknown as { token?: string } | null)?.token;
        if (existingTok) {
          unsubscribeToken = existingTok;

        } else {
          const newToken = crypto.randomUUID().replace(/-/g, "");
          const { data: created } = await supabaseAdmin
            .from("email_unsubscribe_tokens" as never)
            .insert({ email: routed, token: newToken } as never)
            .select("token")
            .single();
          unsubscribeToken = (created as unknown as { token?: string } | null)?.token ?? newToken;
        }
      } catch (e) {
        console.error("[support] unsubscribe token upsert failed", e);
      }


      // Strict preflight — never dispatch a job with missing required fields.
      // The Lovable/Resend API rejects sends without idempotency_key + purpose
      // (that was the 13/07 DLQ cause), and PostgREST/Resend both reject empty
      // from/to/subject/html. Fail fast here so the ticket is still saved but
      // no doomed job is queued (which would just consume 5 retries → DLQ).
      const payload = {
        message_id: messageId,
        idempotency_key: messageId,
        to: routed,
        from: `Recovery Trauma Support <noreply@notify.recoverytrauma.com>`,
        sender_domain: "notify.recoverytrauma.com",
        reply_to: data.email,
        subject,
        html,
        text,
        purpose: "transactional" as const,
        label: "support_request",
        unsubscribe_token: unsubscribeToken,
        queued_at: new Date().toISOString(),
      };

      const REQUIRED_STRING_FIELDS = [
        "message_id",
        "idempotency_key",
        "to",
        "from",
        "sender_domain",
        "subject",
        "html",
        "text",
        "purpose",
        "label",
      ] as const;
      const missing: string[] = [];
      for (const key of REQUIRED_STRING_FIELDS) {
        const v = (payload as Record<string, unknown>)[key];
        if (typeof v !== "string" || v.trim().length === 0) missing.push(key);
      }
      // Recipient sanity: must be a plausible email address.
      if (
        typeof payload.to !== "string" ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.to)
      ) {
        missing.push("to(email-format)");
      }
      // unsubscribe_token is required by the transactional email API for the
      // internal support inbox — treat null/empty as a hard block, not a warn.
      if (!payload.unsubscribe_token || String(payload.unsubscribe_token).length < 8) {
        missing.push("unsubscribe_token");
      }

      if (missing.length > 0) {
        const reason = `Refused to enqueue support email: missing/invalid parameter(s): ${missing.join(", ")}`;
        console.error("[support] preflight validation failed", {
          message_id: messageId,
          missing,
        });
        await supabaseAdmin.from("email_send_log").insert({
          message_id: messageId,
          template_name: "support_request",
          recipient_email: routed,
          status: "failed",
          error_message: reason,
        });
      } else {
        const { error: enqueueError } = await supabaseAdmin.rpc("enqueue_email", {
          queue_name: "transactional_emails",
          payload,
        });
        if (enqueueError) {
          console.error("[support] enqueue failed (ticket saved)", enqueueError);
          await supabaseAdmin.from("email_send_log").insert({
            message_id: messageId,
            template_name: "support_request",
            recipient_email: routed,
            status: "failed",
            error_message: `Enqueue failed: ${enqueueError.message}`,
          });
        }
      }
    } catch (e) {
      console.error("[support] email send failed (ticket saved)", e);
    }


    return {
      ok: true as const,
      id: inserted.id,
      routed_to: routed,
      message_id: `support-${inserted.id}`,
    };
  });

export const getSupportEmailStatus = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) =>
    z.object({ message_id: z.string().min(1).max(200) }).parse(raw),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("email_send_log")
      .select("status,error_message,recipient_email,created_at")
      .eq("message_id", data.message_id)
      .order("created_at", { ascending: false })
      .limit(1);
    if (error) throw new Error(error.message);
    const row = rows?.[0];
    if (!row) return { found: false as const };
    return {
      found: true as const,
      status: row.status as string,
      error_message: (row.error_message as string | null) ?? null,
      recipient_email: row.recipient_email as string,
      created_at: row.created_at as string,
    };
  });

